import { useState } from 'react';

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    jobTitle: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const testimonials = [
    {
      quote: "Covasant transformed our AI strategy from scattered pilots to a unified production roadmap. Within 18 months, we achieved 340% ROI on our AI investments.",
      name: "Michael Torres",
      title: "CIO, Global Financial Services",
      company: "Fortune 500 Bank",
      image: "https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20confident%20male%20executive%20in%20his%2050s%20wearing%20navy%20suit%20neutral%20gray%20background%20corporate%20portrait%20photography%20warm%20lighting&width=200&height=200&seq=testimonial-1&orientation=squarish"
    },
    {
      quote: "Their governance framework gave our board the confidence to approve a $50M AI transformation program. Covasant doesn't just consult—they deliver.",
      name: "Dr. Fatima Al-Rashid",
      title: "CDO, Healthcare Conglomerate",
      company: "Regional Healthcare Leader",
      image: "https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20Middle%20Eastern%20female%20executive%20in%20her%2040s%20wearing%20elegant%20blazer%20neutral%20background%20corporate%20portrait%20photography%20confident%20expression&width=200&height=200&seq=testimonial-2&orientation=squarish"
    },
    {
      quote: "We went from 12 disconnected AI pilots to 3 production-grade systems in under a year. Covasant's methodology is the real deal.",
      name: "James Chen",
      title: "CTO, Manufacturing Enterprise",
      company: "Industrial Leader",
      image: "https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20Asian%20male%20technology%20executive%20in%20his%2040s%20wearing%20charcoal%20blazer%20neutral%20background%20corporate%20portrait%20photography%20professional%20expression&width=200&height=200&seq=testimonial-3&orientation=squarish"
    }
  ];

  const caseStudies = [
    {
      industry: "Financial Services",
      title: "340% ROI in AI-Powered Risk Assessment",
      metric: "340%",
      metricLabel: "ROI Achieved",
      description: "Transformed fragmented AI pilots into a unified risk assessment platform processing 2M+ transactions daily.",
      tags: ["Agentic AI", "Governance", "Production Scale"]
    },
    {
      industry: "Healthcare",
      title: "60% Reduction in Diagnostic Processing Time",
      metric: "60%",
      metricLabel: "Time Saved",
      description: "Deployed AI-powered diagnostic workflows with full regulatory compliance across 45 facilities.",
      tags: ["AI Operations", "Compliance", "Cloud Migration"]
    },
    {
      industry: "Manufacturing",
      title: "$28M Annual Savings via Predictive Maintenance",
      metric: "$28M",
      metricLabel: "Annual Savings",
      description: "Scaled predictive maintenance AI from pilot to 12 production facilities in 9 months.",
      tags: ["Pilot to Production", "IoT Integration", "ROI Framework"]
    }
  ];

  const faqs = [
    {
      question: 'What makes Covasant different from other AI consultancies?',
      answer: 'Covasant specializes exclusively in enterprise AI transformation with a proven track record of helping 200+ organizations move from AI pilots to production. Our frameworks deliver measurable ROI, not just proof-of-concepts.'
    },
    {
      question: 'Who should meet with Covasant at the summit?',
      answer: 'CIOs, CTOs, CDOs, and Enterprise Architects who are serious about scaling AI beyond experimentation. If you have AI pilots that need to become production systems, we should talk.'
    },
    {
      question: 'What is the Executive Roadmap Session?',
      answer: 'A complimentary 30-minute consultation where our experts assess your AI maturity and provide a customized scaling roadmap, governance checkpoints, and ROI alignment framework.'
    },
    {
      question: 'How do I schedule a meeting at Booth TFG 28?',
      answer: 'Click any "Book Meeting" button on this page to select your preferred time slot. You can also book 1:1 sessions with specific Covasant leaders attending the summit.'
    },
    {
      question: 'What is the CIO Leadership Dinner?',
      answer: 'An invitation-only networking dinner for senior executives. To qualify, visit Booth TFG 28 and register your interest. Space is limited to ensure meaningful conversations.'
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://readdy.ai/api/form/d65fa1adtlshu6s4gek0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          fullName: formData.fullName,
          email: formData.email,
          company: formData.company,
          jobTitle: formData.jobTitle
        }).toString()
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({ fullName: '', email: '', company: '', jobTitle: '' });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBrochureDownload = () => {
    window.open('https://readdy.ai/api/search-image?query=Professional%20corporate%20brochure%20cover%20design%20with%20modern%20geometric%20patterns%20navy%20blue%20and%20dark%20gray%20color%20scheme%20enterprise%20technology%20consulting%20theme%20clean%20minimalist%20layout&width=800&height=1000&seq=brochure-preview-navy&orientation=portrait', '_blank');
  };

  return (
    <>
      {/* Client Testimonials Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block text-[#1e3a8a] font-semibold text-sm tracking-wider uppercase mb-4">Client Success</span>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trusted by Enterprise Leaders
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Hear from CIOs and technology executives who transformed their AI strategies with Covasant.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-[#fafaf9] rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 relative"
              >
                <div className="absolute top-6 right-6 text-[#1e3a8a]/10">
                  <i className="ri-double-quotes-r text-6xl"></i>
                </div>
                <p className="text-gray-700 leading-relaxed mb-6 relative z-10">"{testimonial.quote}"</p>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-[#1e3a8a]/20">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-[#1e3a8a]">{testimonial.title}</p>
                    <p className="text-xs text-gray-500">{testimonial.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Highlights Section */}
      <section className="py-24 bg-[#f8f7f5]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block text-[#1e3a8a] font-semibold text-sm tracking-wider uppercase mb-4">Proven Results</span>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Case Study Highlights
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Real enterprise transformations. Measurable outcomes. Production-grade AI at scale.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 group"
              >
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 group">
                  <div className="bg-gradient-to-r from-[#1e3a8a] to-[#3b82f6] p-6">
                    <span className="text-white/70 text-sm font-medium">{study.industry}</span>
                    <div className="mt-4 flex items-end gap-2">
                      <span className="text-5xl font-bold text-white">{study.metric}</span>
                      <span className="text-white/80 text-sm mb-2">{study.metricLabel}</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{study.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">{study.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {study.tags.map((tag, tagIndex) => (
                        <span
                          key={tagIndex}
                          className="px-3 py-1 bg-[#1e3a8a]/10 text-[#1e3a8a] text-xs font-medium rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 bg-[#f0f4f8]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-2">
              <span className="inline-block text-[#1e3a8a] font-semibold text-xs tracking-wider uppercase mb-3">FAQ</span>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Frequently Asked Questions</h2>
              <p className="text-sm text-gray-500 leading-relaxed">Everything you need to know about meeting Covasant at the summit.</p>
            </div>

            <div className="lg:col-span-3 space-y-2">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className={`rounded-lg border transition-all duration-200 ${openIndex === index ? 'bg-white border-gray-200 shadow-sm' : 'bg-transparent border-transparent hover:bg-white hover:border-gray-100'}`}
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                    className="w-full px-5 py-3.5 flex items-center justify-between text-left cursor-pointer gap-3"
                  >
                    <span className="font-medium text-sm text-gray-900">{faq.question}</span>
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full shrink-0 transition-all duration-200 ${openIndex === index ? 'bg-[#1e3a8a] rotate-180' : 'bg-gray-100'}`}>
                      <i className={`ri-arrow-down-s-line text-sm ${openIndex === index ? 'text-white' : 'text-gray-400'}`}></i>
                    </div>
                  </button>
                  <div className={`overflow-hidden transition-all duration-200 ${openIndex === index ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="px-5 pb-4">
                      <p className="text-gray-500 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer with Registration Form and Brochure */}
      <footer className="bg-gray-900 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            {/* Registration Form */}
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">Register for the Summit</h3>
              <p className="text-gray-400 mb-6">Secure your spot and meet Covasant at IDC Middle East CIO Summit 2026.</p>
              
              {submitSuccess ? (
                <div className="bg-[#1e3a8a]/20 border border-[#3b82f6]/30 rounded-xl p-6 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-[#1e3a8a] rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-white"></i>
                  </div>
                  <h4 className="text-xl font-bold text-white mb-2">Registration Successful!</h4>
                  <p className="text-gray-300">We'll be in touch with your summit details shortly.</p>
                </div>
              ) : (
                <form 
                  id="footer-registration-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#3b82f6] transition-colors text-sm"
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Work Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#3b82f6] transition-colors text-sm"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Company *</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#3b82f6] transition-colors text-sm"
                        placeholder="Your Company"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Job Title *</label>
                      <input
                        type="text"
                        name="jobTitle"
                        value={formData.jobTitle}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#3b82f6] transition-colors text-sm"
                        placeholder="CIO, CTO, CDO..."
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-[#1e3a8a] hover:bg-[#1e40af] text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        Registering...
                      </span>
                    ) : (
                      'Register Now'
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Brochure Download */}
            <div className="flex flex-col">
              <h3 className="text-2xl font-bold text-white mb-2">Download Our Brochure</h3>
              <p className="text-gray-400 mb-6">Get the complete Covasant AI Transformation overview and discover how we help enterprises scale.</p>
              
              <div className="flex-1 bg-gray-800 rounded-xl p-6 border border-gray-700 flex flex-col">
                <div className="flex-1 flex items-center justify-center mb-6">
                  <div className="relative group">
                    <div className="w-48 h-64 bg-gradient-to-br from-[#1e3a8a]/20 to-gray-700 rounded-lg shadow-2xl flex items-center justify-center border border-gray-600 overflow-hidden">
                      <div className="text-center p-4">
                        <div className="w-12 h-12 flex items-center justify-center bg-[#1e3a8a]/30 rounded-full mx-auto mb-3">
                          <i className="ri-file-pdf-2-line text-2xl text-[#60a5fa]"></i>
                        </div>
                        <p className="text-white font-bold text-sm mb-1">COVASANT</p>
                        <p className="text-gray-400 text-xs">AI Transformation Brochure</p>
                        <p className="text-gray-500 text-xs mt-2">2026 Edition</p>
                      </div>
                    </div>
                    <div className="absolute -bottom-2 -right-2 w-48 h-64 bg-gray-700/50 rounded-lg -z-10 transform rotate-3"></div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-gray-300 text-sm">
                    <i className="ri-check-line text-[#60a5fa]"></i>
                    <span>Enterprise AI Scaling Frameworks</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300 text-sm">
                    <i className="ri-check-line text-[#60a5fa]"></i>
                    <span>Governance & Compliance Guidelines</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300 text-sm">
                    <i className="ri-check-line text-[#60a5fa]"></i>
                    <span>ROI Measurement Methodologies</span>
                  </div>
                </div>
                
                <button
                  onClick={handleBrochureDownload}
                  className="mt-6 w-full py-4 bg-white hover:bg-gray-100 text-gray-900 font-semibold rounded-lg transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-download-2-line text-lg"></i>
                  Download Brochure
                </button>
              </div>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-2xl font-bold text-white">COVASANT</div>
              
              {/* Social Links */}
              <div className="flex items-center gap-4">
                <a 
                  href="https://linkedin.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center bg-gray-800 hover:bg-[#1e3a8a] rounded-full transition-colors cursor-pointer"
                >
                  <i className="ri-linkedin-fill text-white text-lg"></i>
                </a>
                <a 
                  href="https://twitter.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center bg-gray-800 hover:bg-[#1e3a8a] rounded-full transition-colors cursor-pointer"
                >
                  <i className="ri-twitter-x-fill text-white text-lg"></i>
                </a>
                <a 
                  href="https://youtube.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center bg-gray-800 hover:bg-[#1e3a8a] rounded-full transition-colors cursor-pointer"
                >
                  <i className="ri-youtube-fill text-white text-lg"></i>
                </a>
                <a 
                  href="mailto:contact@covasant.com"
                  className="w-10 h-10 flex items-center justify-center bg-gray-800 hover:bg-[#1e3a8a] rounded-full transition-colors cursor-pointer"
                >
                  <i className="ri-mail-fill text-white text-lg"></i>
                </a>
              </div>

              <div className="flex items-center gap-6 text-sm text-gray-500">
                <span>© 2026 Covasant. All rights reserved.</span>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="nofollow" className="hover:text-gray-300 transition-colors cursor-pointer">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
